import * as React from 'react';
import classnames from "classnames";

import { Quantity } from "../../../../rest/Misc";
import { assertTrue, assertNumber, assertString } from "../../../utils/assert";
import { intToString, stringToInt } from '../../../../converters/converters'
import { Sensor, SensorDraft, IdName, GeotabSensor, Key } from '../../../../models';
import { ServiceAccount } from '../../../../models/ServiceAccount';
import { Vehicle, Source } from '../../../../models/Vehicle';
import { Select } from '../../../../components/Select'

import { IForm } from "../IForm";
import { Loadable } from "../EditCreate/Loadable";

import { EditSensorActions, EditSensorStoreState } from "./flux";
import { QuantitySelector } from "./EditCreateSensor/QuantitySelector";
import { VehicleSelector } from "./EditCreateSensor/VehicleSelector";
import { NativeSensorSelector } from "./EditCreateSensor/NativeSensorSelector";
import { FormItemHorizontal } from "../../Forms/FormItemHorizontal";
import { Polyglot } from '../../../../polyglot';
import { LoadableGroup, LabelGroup, InputTextGroup, LevelGroup } from "../../Forms/Groups";
import { Command1 } from "../../../../Command";

const styles = require('./NewSensor.scss');

interface FormProps
{
    polyglot: Polyglot;
    loadingVehicles: boolean;
    loadingGeotabSensors: boolean;
    loadingQuantities: boolean;

    setVehicleCmd: Command1<Key>;
    setGeotabSensorCmd: Command1<GeotabSensor>;
}

// Partial because these props will be injected
type Props = Partial<EditSensorStoreState> & Partial<EditSensorActions> & FormProps;
export class NewSensor extends React.Component<Props> implements IForm
{
    getServiceAccountValue = (account: ServiceAccount) => intToString(account.id);
    getServiceAccoutDisplay = (account: ServiceAccount) => account.Username;

    getVehicleValue = (v: Vehicle) => intToString(v.id);
    getPairValue = (pair: IdName) => pair.Id;

    render ()
    {
        const { polyglot, draft, vehicles, geotabSensors, quantities, units } = this.props;
        const geotab = geotabSensors ? geotabSensors.find(item => item.Id === draft.GeotabId) : undefined;
        const selectedGeotab = geotab ? [geotab] : [];

        const quantity = quantities.find(item => item.Id === draft.QuantityId);
        const isRelative = quantity && quantity.RelativeLevels;

        const unit = units.find(item => item.Id === draft.UnitId);
        const unitName = unit ? unit.Name : "";

        const { CanOnlyEditLevels } = draft;

        return (
            <div className={classnames("form-horizontal", styles.NewSensor)}>
                { CanOnlyEditLevels && <LabelGroup title={polyglot.t('sensor.label_vehicle') + ':'} value={draft.Vehicle} /> }
                { !CanOnlyEditLevels &&
                    <LoadableGroup title={polyglot.t('sensor.label_vehicle') + ':'} value={draft.Vehicle} isLoading={this.props.loadingVehicles}>
                        <VehicleSelector
                            polyglot={polyglot}
                            vehicleId={draft.VehicleId} vehicles={vehicles}
                            setVehicle={this.props.setVehicleCmd.execute} />
                    </LoadableGroup>
                }

                { CanOnlyEditLevels && <LabelGroup title={polyglot.t('sensor.label_sensor') + ':'} value={draft.GeotabTitle} /> }
                { !CanOnlyEditLevels &&
                    <LoadableGroup title={polyglot.t('sensor.label_sensor') + ':'} value={draft.GeotabTitle} isLoading={this.props.loadingGeotabSensors}>
                        <NativeSensorSelector
                            polyglot={polyglot}
                            disabled={this.props.draft.VehicleId == null}
                            nativeSensorId={draft.GeotabId} nativeSensors={geotabSensors}
                            setNativeSensor={this.props.setGeotabSensorCmd.execute} />
                    </LoadableGroup>
                }

                { CanOnlyEditLevels && <LabelGroup title={polyglot.t('sensor.label_display') + ':'} value={draft.Display} /> }
                { !CanOnlyEditLevels && <InputTextGroup title={polyglot.t('sensor.label_display') + ':'} value={draft.Display} onChange={e => this.props.setDisplay(e.target.value)} /> }

                { CanOnlyEditLevels && <LabelGroup title={polyglot.t('sensor.label_quantity') + ':'} value={getQuantityName(quantities, draft.QuantityId)} /> }
                { !CanOnlyEditLevels &&
                    <LoadableGroup title={polyglot.t('sensor.label_quantity') + ':'} value={getQuantityName(quantities, draft.QuantityId)} isLoading={this.props.loadingQuantities}>
                        <QuantitySelector
                            disabled={this.props.draft.GeotabId == null}
                            quantities={quantities} quantityId={draft.QuantityId} setQuantity={this.props.setQuantity} />
                    </LoadableGroup>
                }

                { isRelative &&
                    <LevelGroup title={polyglot.t('levels.baseline') + ':'} unitName={unitName}
                        value={intToString(draft.Baseline, true)}
                        onChange={e => this.props.setBaseline(stringToInt(e.target.value, true))}
                    />
                }
                { !isRelative && <div className='form-group' style={{height: 34}} />}

                <LevelGroup title={polyglot.t('levels.red_max') + ':'} unitName={isRelative ? '+%' : unitName}
                    value={intToString(isRelative ? draft.RelLevelMaximumRed : draft.AbsLevelMaximumRed, true)}
                    onChange={e => {
                        const { setRelRedMax, setAbsRedMax } = this.props;
                        const handler = isRelative ? setRelRedMax : setAbsRedMax;
                        handler(stringToInt(e.target.value, true));
                    }}
                />

                <LevelGroup title={polyglot.t('levels.yellow_max') + ':'} unitName={isRelative ? '+%' : unitName}
                    value={intToString(isRelative ? draft.RelLevelMaximumYellow : draft.AbsLevelMaximumYellow, true)}
                    onChange={e => {
                        const { setAbsYellowMax, setRelYellowMax } = this.props;
                        const handler = isRelative ? setRelYellowMax : setAbsYellowMax;
                        handler(stringToInt(e.target.value, true));
                    }}
                />

                <LevelGroup title={polyglot.t('levels.yellow_min') + ':'} unitName={isRelative ? '-%' : unitName}
                    value={intToString(isRelative ? draft.RelLevelMinimumYellow : draft.AbsLevelMinimumYellow, true)}
                    onChange={e => {
                            const { setAbsYellowMin, setRelYellowMin } = this.props;
                            const handler = isRelative ? setRelYellowMin : setAbsYellowMin;
                            handler(stringToInt(e.target.value, true));
                    }}
                />

                <LevelGroup title={polyglot.t('levels.red_min') + ':'} unitName={isRelative ? '-%' : unitName}
                    value={intToString(isRelative ? draft.RelLevelMinimumRed : draft.AbsLevelMinimumRed, true)}
                    onChange={e => {
                            const { setAbsRedMin, setRelRedMin } = this.props;
                            const handler = isRelative ? setRelRedMin : setAbsRedMin;
                            handler(stringToInt(e.target.value, true));
                    }}
                />
            </div>
        );
    }

    getValue (): SensorDraft
    {
        return {...this.props.draft};
    }
}
const getQuantityName = (quantities: Quantity[], id: Key) => {
    const quantity = quantities.find(q => q.Id === id);
    return quantity ? quantity.Name : '';
}


// WEBPACK FOOTER //
// ./src/app/components/Pages/Sensor/NewSensor.tsx